import { PLATFORM_LEGAL_PATHS, type PortalViewOutput } from '@garage/shared';
import { HELP_HOME } from '../../help/paths.js';
import type { ReactNode } from 'react';
import { BrandMark } from '../../shared/components/BrandMark.js';
import { Plate } from '../../ui/Plate.js';
import { cx } from '../../ui/cx.js';
import { PortalVehiclePhoto } from './PortalVehiclePhoto.js';

/** Bloc d'état en tête d'onglet : bord à bord sur téléphone, carte sur écran plus large. */
export const HERO = 'border-b border-line bg-surface px-4 py-5 sm:rounded-2xl sm:border sm:p-6';

/** Carte du portail : surface, filet. */
export const CARD = 'rounded-2xl border border-line bg-surface p-(--card-p)';

export function Block({ title, children, id, className }: { title: string; children: ReactNode; id?: string; className?: string }) {
  return (
    <section id={id} className={cx('scroll-mt-4 px-4 pt-7 sm:px-0', className)}>
      <h2 className="mb-2.5 text-lg font-semibold tracking-[-0.01em] text-ink">{title}</h2>
      {children}
    </section>
  );
}

/** En-tête à la marque du garage (téléphone et aperçu) : blason ou monogramme, nom du garage. */
export function BrandHeader({ garage }: { garage: PortalViewOutput['garage'] }) {
  return (
    <header className="border-b-2 border-masthead-line bg-masthead pt-[env(safe-area-inset-top)] text-on-masthead">
      <div className="mx-auto flex min-h-16 max-w-2xl items-center gap-3 px-4 py-3 sm:px-0">
        <BrandMark name={garage.displayName} logoUrl={garage.logoUrl} iconUrl={garage.iconUrl ?? null} size="lg" />
        <span className="min-w-0 text-lg font-semibold tracking-[-0.01em]">{garage.displayName}</span>
      </div>
    </header>
  );
}

export function VehicleText({ vehicle, className }: { vehicle: PortalViewOutput['vehicle']; className?: string }) {
  return (
    <p className={cx('flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-ink-2', className)}>
      <span>
        {vehicle.make} {vehicle.model}
        {vehicle.version ? ` ${vehicle.version}` : ''}
      </span>
      <Plate plate={vehicle.plate} size="sm" />
    </p>
  );
}

export function VehicleLine({ vehicle }: { vehicle: PortalViewOutput['vehicle'] }) {
  return (
    <>
      {vehicle.photo ? <PortalVehiclePhoto alt={`${vehicle.make} ${vehicle.model}`} /> : null}
      <VehicleText vehicle={vehicle} />
    </>
  );
}

/**
 * Vague 6 : lien « Confidentialité » en pied du portail (politique du garage, page publique par clé).
 * Adresse fournie par l'API (`privacyUrl`) ; absente tant que le serveur ne la renvoie pas.
 */
export function PrivacyFooter({ url }: { url: string | null }) {
  const link = 'inline-flex min-h-(--ctl-sm) items-center px-2 underline underline-offset-2 hover:text-ink';
  // 08/10/2026 : « Aide » (centre d'aide du client, public, sans marque de garage).
  // Vague 7 : notice du garage (responsable), puis pages légales de la plateforme (éditeur, sous-traitant).
  return (
    <footer className="mt-10 border-t border-line px-4 pt-4 text-center text-sm text-ink-3 sm:px-0">
      <nav aria-label="Informations légales">
        <ul className="flex flex-wrap items-center justify-center gap-x-1">
          {url ? (
            <li>
              <a href={url} className={`${link} font-medium text-ink-2`}>
                Confidentialité
              </a>
            </li>
          ) : null}
          <li>
            <a href={HELP_HOME.client} className={`${link} font-medium text-ink-2`}>
              Aide
            </a>
          </li>
          <li>
            <a href={PLATFORM_LEGAL_PATHS.legalNotice} className={link}>
              Mentions légales
            </a>
          </li>
          <li>
            <a href={PLATFORM_LEGAL_PATHS.privacy} className={link}>
              Confidentialité de la plateforme
            </a>
          </li>
        </ul>
      </nav>
    </footer>
  );
}
